import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import cookie from "react-cookies";
import { getCreatorScript, startCreatorScript } from "../../../services/creatorApi";
import { TrainingLoading } from "../../academy/AcademyShared";
import { CreatorLayout, CREATOR_CRUMBS } from "./CreatorShared";
import { handleCreatorGateError } from "./creatorLicense";
import { getScriptBrief, normalizeTextList } from "./creatorModels";

function ShotGuideRow({ label, value }) {
  if (!value) return null;
  return (
    <div className="creator-brief-row">
      <span className="creator-brief-row__label">{label}</span>
      <span className="creator-brief-row__value">{value}</span>
    </div>
  );
}

function BriefField({ label, value, prominent = false }) {
  const items = normalizeTextList(value);
  if (!items.length) return null;
  return (
    <div className={`creator-v2-field${prominent ? " creator-v2-field--prominent" : ""}`}>
      <h4>{label}</h4>
      {items.length === 1 ? (
        <p>{items[0]}</p>
      ) : (
        <ul>
          {items.map((item, index) => (
            <li key={`${label}-${index}`}>{item}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ReferenceImages({ images, label, onOpen }) {
  if (!images?.length) return null;
  return (
    <div className="creator-brief-previews">
      <p className="creator-brief-say-label">Look reference (strict)

</p>
      <div className="creator-brief-preview-grid">
        {images.map((src, index) => (
          <button
            key={`${label}-${index}`}
            type="button"
            className="creator-brief-preview"
            onClick={() => onOpen({ src, label, idx: index + 1 })}
          >
            <img src={src} alt={`${label} reference ${index + 1}`} loading="lazy" />
            <span className="creator-brief-preview__cap">Ref {index + 1}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function DoAvoid({ doThis, avoidThis }) {
  return (
    <div className="creator-v2-do-avoid">
      {doThis ? (
        <div className="creator-brief-callout creator-brief-callout--do">
          <strong>Do this</strong>
          <p>{doThis}</p>
        </div>
      ) : null}
      {avoidThis ? (
        <div className="creator-brief-callout creator-brief-callout--avoid">
          <strong>Avoid this</strong>
          <p>{avoidThis}</p>
        </div>
      ) : null}
    </div>
  );
}

function V2Brief({ brief, onOpen }) {
  return (
    <>
      <section className="creator-v2-intro" aria-labelledby="hook-performance-title">
        <span className="creator-v2-step">Part 1 of 2</span>
        <h3 id="hook-performance-title">Record all 3 hook performances</h3>
        <p>
          Record each hook as its own vertical video. Follow the exact words and performance
          direction so QikTruck can test three different openings.
        </p>
      </section>

      <div className="creator-shot-list">
        {brief.hook_variants.map((hook, index) => (
          <article
            key={hook.deliverable_key || index}
            className="training-card creator-brief-card creator-v2-hook"
          >
            <div className="creator-shot-card__head">
              <span className="creator-shot-card__num">Hook {String.fromCharCode(65 + index)}</span>
              <span className="creator-chip creator-chip--slate">Separate video</span>
            </div>
            <BriefField label="Exact spoken hook" value={hook.spoken_hook} prominent />
            <BriefField label="On-screen text" value={hook.text_hook} prominent />
            <div className="creator-v2-field-grid">
              <BriefField label="Opening frame" value={hook.opening_frame} />
              <BriefField label="Action" value={hook.creator_action} />
              <BriefField label="Expression change" value={hook.facial_expression} />
              <BriefField label="Gesture" value={hook.gesture} />
              <BriefField label="Prop" value={hook.prop} />
              <BriefField label="Camera" value={hook.camera} />
              <BriefField label="Transition" value={hook.transition} />
              <BriefField label="Payoff" value={hook.payoff} />
            </div>
            <DoAvoid doThis={hook.do_this} avoidThis={hook.avoid_this} />
            <ReferenceImages
              images={hook.preview_image_urls}
              label={`Hook ${String.fromCharCode(65 + index)}`}
              onOpen={onOpen}
            />
          </article>
        ))}
      </div>

      <section className="creator-v2-intro" aria-labelledby="shared-body-title">
        <span className="creator-v2-step">Part 2 of 2</span>
        <h3 id="shared-body-title">Record the shared body once</h3>
        <p>
          This is one separate performance. Start after the hook and keep the look, energy,
          framing and props consistent across all four videos.
        </p>
      </section>

      <article className="training-card creator-brief-card creator-v2-body">
        <BriefField label="Creator wording" value={brief.shared_body.creator_wording} prominent />
        <BriefField label="Story" value={brief.shared_body.story} />
        <BriefField label="Guided beats" value={brief.shared_body.beats} />
        <BriefField label="Must-says" value={brief.shared_body.must_says} />
        <div className="creator-v2-field-grid">
          <BriefField label="Proof" value={brief.shared_body.proof} />
          <BriefField label="CTA" value={brief.shared_body.cta} />
          <BriefField label="Allowed claims" value={brief.shared_body.allowed_claims} />
          <BriefField label="Banned claims" value={brief.shared_body.banned_claims} />
          <BriefField label="Disclosure" value={brief.shared_body.disclosure} />
          <div className="creator-brief-field">
            <BriefField label="Rights" value={brief.shared_body.rights} />
            <p className="creator-brief-license-link">
              <Link to="/creator/license">Read full Creator Content License</Link>
            </p>
          </div>
          <BriefField label="Length and specs" value={brief.shared_body.length_specs} />
          <BriefField label="Continuity" value={brief.shared_body.continuity} />
        </div>
        <DoAvoid
          doThis={brief.shared_body.do_this}
          avoidThis={brief.shared_body.avoid_this}
        />
      </article>
    </>
  );
}

export default function CreatorScriptDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!cookie.load("candidateKey")) {
      window.location.href = `/login?next=/me/creator/scripts/${id}`;
      return;
    }
    setLoading(true);
    getCreatorScript(id)
      .then(setData)
      .catch((e) => setError(e.message || "Could not load script"))
      .finally(() => setLoading(false));
  }, [id]);

  const onStart = async () => {
    setBusy(true);
    setError("");
    try {
      if (data?.my_submission?.id) {
        navigate(`/me/creator/work/${data.my_submission.id}`);
        return;
      }
      const res = await startCreatorScript(id);
      navigate(`/me/creator/work/${res.submission.id}`);
    } catch (e) {
      if (handleCreatorGateError(e, "/me/creator")) return;
      if (e?.code === "script_expired") {
        setError(e.message || "This script is no longer available.");
        return;
      }
      setError(e.message || "Could not start script");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <CreatorLayout
        title="Script"
        breadcrumbs={CREATOR_CRUMBS.scriptDetail("Script")}
        backTo="/me/creator"
      >
        <TrainingLoading label="Loading brief..." />
      </CreatorLayout>
    );
  }

  if (!data?.script) {
    return (
      <CreatorLayout
        title="Script"
        breadcrumbs={CREATOR_CRUMBS.scriptDetail("Script")}
        backTo="/me/creator"
      >
        <div className="training-empty">
          <p>{error || "Script not found."}</p>
          <Link className="training-btn-primary" to="/me/creator">
            Back to scripts
          </Link>
        </div>
      </CreatorLayout>
    );
  }

  const { script, shots = [] } = data;
  const v2Brief = getScriptBrief(data);

  return (
    <CreatorLayout
      title={script.code}
      breadcrumbs={CREATOR_CRUMBS.scriptDetail(`${script.code}`)}
      backTo="/me/creator"
    >
      <section className="creator-hero">
        <p className="training-kicker">{script.category}</p>
        <h2 className="creator-hero__title">
          {script.code} · {script.title}
        </h2>
        <p className="creator-hero__lede">
          Payment {script.payment_label} after QikTruck approval.{" "}
          {v2Brief
            ? `This brief needs ${v2Brief.deliverable_label}. Each performance is uploaded separately.`
            : "Nail the spoken line first. Reference images are optional look cues."}
        </p>
        {script.instructions_md ? (
          <p className="creator-tip">{script.instructions_md}</p>
        ) : null}
        {!v2Brief ? (
          <p className="creator-tip">
            Each shot leads with what you say. Small look references sit underneath if you want framing ideas.
          </p>
        ) : null}
      </section>

      {error ? <p className="creator-error">{error}</p> : null}

      {v2Brief ? (
        <div className="creator-v2-brief creator-shot-list--pad">
          <V2Brief brief={v2Brief} onOpen={setLightbox} />
        </div>
      ) : (
      <div className="creator-shot-list creator-shot-list--pad">
        {shots.map((shot) => {
          const previews = shot.preview_image_urls || [];
          return (
            <article key={shot.id || shot.shot_number} className="training-card creator-shot-card creator-brief-card">
              <div className="creator-shot-card__head">
                <span className="creator-shot-card__num">Shot {shot.shot_number} of {shots.length}</span>
              </div>

              <div className="creator-say-panel">
                <p className="creator-brief-say-label">Say this</p>
                <p className="creator-shot-card__line">&ldquo;{shot.exact_sentence}&rdquo;</p>
              </div>

              <div className="creator-brief-guide">
                <ShotGuideRow label="Camera" value={shot.camera_angle} />
                <ShotGuideRow label="Posture" value={shot.posture} />
                <ShotGuideRow label="Gesture" value={shot.gesture} />
                <ShotGuideRow label="Expression" value={shot.expression_tone} />
                <ShotGuideRow label="Props / place" value={shot.location_props} />
              </div>

              {previews.length > 0 ? (
                <div className="creator-brief-previews">
                  <p className="creator-brief-say-label">Look reference (strict)

</p>
                  <div className="creator-brief-preview-grid">
                    {previews.map((src, idx) => (
                      <button
                        key={`${shot.shot_number}-${idx}`}
                        type="button"
                        className="creator-brief-preview"
                        onClick={() => setLightbox({ src, shot: shot.shot_number, idx: idx + 1 })}
                      >
                        <img src={src} alt={`Shot ${shot.shot_number} example ${idx + 1}`} loading="lazy" />
                        <span className="creator-brief-preview__cap">Ref {idx + 1}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : null}

              {shot.do_this ? (
                <div className="creator-brief-callout creator-brief-callout--do">
                  <strong>Do this</strong>
                  <p>{shot.do_this}</p>
                </div>
              ) : null}
              {shot.avoid_this ? (
                <div className="creator-brief-callout creator-brief-callout--avoid">
                  <strong>Avoid this</strong>
                  <p>{shot.avoid_this}</p>
                </div>
              ) : null}
            </article>
          );
        })}
      </div>
      )}

      {lightbox ? (
        <div
          className="creator-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={`${lightbox.label || `Shot ${lightbox.shot}`} example`}
          onClick={() => setLightbox(null)}
        >
          <button type="button" className="creator-lightbox__close" onClick={() => setLightbox(null)}>
            Close
          </button>
          <img
            src={lightbox.src}
            alt={`${lightbox.label || `Shot ${lightbox.shot}`} example ${lightbox.idx}`}
          />
        </div>
      ) : null}

      {data.script?.is_expired ? (
        <div
          className="creator-brief-callout creator-brief-callout--avoid"
          role="status"
          style={{ margin: "0 0 16px" }}
        >
          <strong>This script is no longer available</strong>
          <p>
            {data.script.unavailable_message ||
              "The deadline has passed, so new uploads are closed. You can still read the brief."}
          </p>
        </div>
      ) : null}

      {data.script?.script_blocked && !data.my_submission?.id && !data.script?.is_expired ? (
        <div className="creator-brief-callout creator-brief-callout--avoid" style={{ margin: "0 0 16px" }}>
          <strong>This script is closed for now</strong>
          <p>
            {data.script_block?.block_note ||
              "This one did not pass review, so you cannot start it again yet. Other scripts are still open. If we unlock this script, you can film it again."}
          </p>
        </div>
      ) : null}

      <div className="creator-sticky-actions">
        {data.my_submission?.id ? (
          <button
            type="button"
            className="training-btn-primary creator-sticky-actions__btn"
            disabled={busy}
            onClick={onStart}
          >
            {data.script?.is_expired && data.my_submission.status === "in_progress"
              ? "View my work"
              : "Continue recording"}
          </button>
        ) : data.script?.is_expired ? (
          <span className="training-btn-secondary creator-sticky-actions__btn">
            No longer available
          </span>
        ) : data.script?.script_blocked ? (
          <span className="training-btn-secondary creator-sticky-actions__btn">
            Unavailable for now
          </span>
        ) : data.filming_unlocked === false ? (
          <Link
            className="training-btn-primary creator-sticky-actions__btn"
            to="/me/creator/intro"
          >
            Record intro first
          </Link>
        ) : (
          <button
            type="button"
            className="training-btn-primary creator-sticky-actions__btn"
            disabled={busy}
            onClick={onStart}
          >
            {busy ? "Starting..." : "Start script"}
          </button>
        )}
      </div>
    </CreatorLayout>
  );
}
